import ArticleHeader from 'components/general/ArticleHeader/ArticleHeader';
import ContentMatrix from 'components/general/ContentMatrix/ContentMatrix';
import DateTime, { DateTimeProps } from 'components/general/DateTime/DateTime';
import Flash from 'components/general/Flash/Flash';
import Meta from 'components/general/Meta/Meta';
import Profile from 'components/general/Profile/Profile';
import Share from 'components/general/Share/Share';
import SummaryList from 'components/general/SummaryList/SummaryList';
import Can from 'containers/Can/Can';
import SiteImage from 'containers/SiteImage';
import * as R from 'ramda';
import React, { Fragment } from 'react';
import LinkProps from 'utils/ts/LinkProps';
import RenderPropType from 'utils/ts/RenderPropType';

const generateMeta = data => (
  R.reject(R.isNil)(
    [
      (
        R.prop('created', data)
          ? {
            id: 'when',
            title: 'When',
            info: (
              <DateTime {...R.prop('created', data)} />
            ),
          }
          : undefined
      ),
      (
        R.path(['author', 'name'], data)
          ? {
            id: 'editor',
            title: 'Editor',
            info: (
              <Profile
                name={R.path(['author', 'name'], data)}
                photo={R.path(['author', 'photo'], data)}
                to={{
                  type: 'ARTICLES_INDEX',
                  query: {
                    author: R.path(['author', 'id'], data),
                  },
                }}
              />
            ),
          }
          : undefined
      ),
      ...R.propOr([], 'meta')(data),
      (
        R.prop('share', data)
          ? {
            id: 'share',
            title: 'Share',
            info: (
              <Share
                title={R.prop('title', data)}
                url={R.prop('permalink', data)}
              />
            ),
          }
          : undefined
      ),
    ])
);

const Article: React.SFC<PropDef> = ({
  title,
  coverImage,
  category,
  summary,
  matrixBlocks,
  renderCoverImage,
  marketingUrl,
  ...args
}) => (
  <article
    itemScope
    itemType="http://schema.org/Article"
    className="article"
  >
    {(renderCoverImage || coverImage) && (
      <Fragment>
        {renderCoverImage
          ? renderCoverImage({})
          : (
            <SiteImage
              src={coverImage.src}
              crops={[
                { w: 320, h: 80 },
                { w: 640, h: 160 },
                { w: 1280, h: 320 },
                { w: 1920, h: 480 },
              ]}
              alt={coverImage.alt}
            />
          )
        }
      </Fragment>
    )}
    <div className="article__content">
      <ArticleHeader
        category={category}
        title={title}
        summary={summary}
        className="article__header"
      />
      <div className="article__body" itemProp="articleBody">
        <div className="article__meta">
          <SummaryList
            items={generateMeta(args)}
            renderItem={MetaItem}
          />
        </div>
        <ContentMatrix blocks={matrixBlocks} classParent="article" />
        <Can action="use" on="login">
          <footer className="article__footer">
            <Flash
              type="accent"
              message="S-RM’s GSI is the simplest way to get a fresh perspective on
                the security risks affecting you, your work, and your travel."
              actions={[{
                title: 'Find out more',
                types: ['accent'],
                to: marketingUrl,
              }]}
            />
          </footer>
        </Can>
      </div>
    </div>
  </article>
);

const MetaItem = item => (
  <Meta
    title={item.title}
    info={item.info}
  />
);

interface PropDef {
  title: string;
  category: {
    title: string;
    to: LinkProps;
  };
  matrixBlocks: object[];
  author?: {
    name: string;
    photo: string;
  };
  permalink?: string;
  summary?: string;
  renderCoverImage: RenderPropType;
  coverImage?: {
    src: string;
    alt: string;
  };
  published: DateTimeProps;
  marketingUrl: string;
}

export default Article;

